import React, { useEffect, useState } from 'react';
import { incidentsApi } from '../../lib/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
  AreaChart, Area, CartesianGrid,
} from 'recharts';
import {
  Activity, Loader2, ShieldAlert, Clock, TrendingDown, TrendingUp,
} from 'lucide-react';

/**
 * Practice-admin dashboard widget: incident trends.
 *
 *  - Falls per 1,000 patient-days (key safety KPI used in Joint Commission /
 *    CMS reporting).
 *  - Mean time to triage (MTTR) — minutes between report and acknowledgement.
 *  - Top incident types and a 30/90-day daily trend.
 *
 * Hides itself if there are zero incidents in the period to avoid empty noise
 * on quiet days.
 */
export default function IncidentTrendsWidget() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    incidentsApi.analytics(days)
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [days]);

  if (loading) {
    return (
      <Card className="careiq-card" data-testid="incident-trends-loading">
        <CardContent className="p-6 flex items-center gap-2 text-[#64748B]">
          <Loader2 className="w-4 h-4 animate-spin" />Loading incident analytics…
        </CardContent>
      </Card>
    );
  }
  if (!data || data.total === 0) {
    return (
      <Card className="careiq-card" data-testid="incident-trends-empty">
        <CardContent className="p-6 text-center text-[#64748B] flex flex-col items-center gap-2">
          <ShieldAlert className="w-8 h-8 text-[#16A34A]" />
          <div className="text-sm">No incidents reported in the last {days} days. Great job, team.</div>
        </CardContent>
      </Card>
    );
  }

  const formatMttr = (m) => {
    if (m == null) return '—';
    if (m < 60) return `${Math.round(m)}m`;
    if (m < 60 * 24) return `${(m / 60).toFixed(1)}h`;
    return `${(m / (60 * 24)).toFixed(1)}d`;
  };

  return (
    <Card className="careiq-card" data-testid="incident-trends-widget">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-semibold text-[#0A2342] flex items-center gap-2">
            <Activity className="w-5 h-5 text-[#0E7490]" />Patient Incident Trends
          </h3>
          <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v))}>
            <SelectTrigger className="w-32 h-8" data-testid="trends-period-trigger"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="180">Last 180 days</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="p-3 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]" data-testid="kpi-total">
            <div className="text-[10px] uppercase text-[#64748B] font-bold tracking-wider">Total incidents</div>
            <div className="text-2xl font-bold text-[#0A2342] mt-1">{data.total}</div>
            <div className="text-[10px] text-[#94A3B8] mt-0.5">{data.active_patients} active patients</div>
          </div>
          <div className="p-3 rounded-lg bg-[#FEE2E2] border border-[#FECACA]" data-testid="kpi-falls">
            <div className="text-[10px] uppercase text-[#991B1B] font-bold tracking-wider">Falls / 1,000 PD</div>
            <div className="text-2xl font-bold text-[#991B1B] mt-1">{data.falls_per_1000_patient_days}</div>
            <div className="text-[10px] text-[#991B1B]/70 mt-0.5">{data.falls_total} fall{data.falls_total === 1 ? '' : 's'} this period</div>
          </div>
          <div className="p-3 rounded-lg bg-[#DBEAFE] border border-[#BFDBFE]" data-testid="kpi-mttr">
            <div className="text-[10px] uppercase text-[#1E40AF] font-bold tracking-wider flex items-center gap-1"><Clock className="w-3 h-3" />Triage MTTR</div>
            <div className="text-2xl font-bold text-[#1E40AF] mt-1">{formatMttr(data.mean_time_to_triage_minutes)}</div>
            <div className="text-[10px] text-[#1E40AF]/70 mt-0.5">{data.triaged_count} triaged of {data.total}</div>
          </div>
          <div className="p-3 rounded-lg bg-[#FEF3C7] border border-[#FDE68A]" data-testid="kpi-open">
            <div className="text-[10px] uppercase text-[#92400E] font-bold tracking-wider">Open</div>
            <div className="text-2xl font-bold text-[#92400E] mt-1">{data.status_breakdown.open}</div>
            <div className="text-[10px] text-[#92400E]/70 mt-0.5">{data.status_breakdown.acknowledged} acked · {data.status_breakdown.resolved} resolved</div>
          </div>
        </div>

        {/* Daily trend area chart */}
        <div className="p-3 rounded-lg bg-white border border-[#E2E8F0]">
          <div className="text-xs font-semibold text-[#0A2342] mb-2">Incidents per day</div>
          <ResponsiveContainer width="100%" height={120}>
            <AreaChart data={data.daily_trend}>
              <defs>
                <linearGradient id="incTrend" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#DC2626" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#DC2626" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
              <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 10, fill: '#64748B' }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#64748B' }} width={24} />
              <Tooltip cursor={{ stroke: '#E2E8F0' }} contentStyle={{ fontSize: 11, padding: 6 }} />
              <Area type="monotone" dataKey="count" stroke="#DC2626" fill="url(#incTrend)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Top types horizontal bar chart */}
        <div className="p-3 rounded-lg bg-white border border-[#E2E8F0]">
          <div className="text-xs font-semibold text-[#0A2342] mb-2">Top incident types</div>
          <ResponsiveContainer width="100%" height={Math.max(80, data.top_types.length * 28)}>
            <BarChart data={data.top_types} layout="vertical" margin={{ left: 10, right: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: '#64748B' }} />
              <YAxis type="category" dataKey="label" tick={{ fontSize: 11, fill: '#0A2342' }} width={140} />
              <Tooltip contentStyle={{ fontSize: 11, padding: 6 }} formatter={(v, _, p) => [`${v} (${p.payload.pct}%)`, 'Count']} />
              <Bar dataKey="count" fill="#0E7490" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Severity bar */}
        <div className="grid grid-cols-4 gap-2" data-testid="severity-breakdown">
          {[
            { k: 'critical', c: '#7F1D1D', bg: '#FEE2E2' },
            { k: 'high', c: '#DC2626', bg: '#FEE2E2' },
            { k: 'medium', c: '#CA8A04', bg: '#FEF3C7' },
            { k: 'low', c: '#16A34A', bg: '#DCFCE7' },
          ].map(({ k, c, bg }) => (
            <div key={k} className="p-2 rounded-lg text-center" style={{ backgroundColor: bg }}>
              <div className="text-lg font-bold" style={{ color: c }}>{data.severity_breakdown[k] || 0}</div>
              <div className="text-[10px] uppercase tracking-wider" style={{ color: c }}>{k}</div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
